import { api, ApiError } from "@/lib/api";

export type WecomOutboundMessage = {
  id: number;
  external_userid: string;
  sender_userid: string;
  content: string;
  status: "pending_confirm" | "sent" | "failed" | "cancelled" | string;
  msgid: string | null;
  error_message: string | null;
  headline: string;
  sent_at?: string | null;
  created_at: string;
  journey_customer_binding_id?: number | null;
  journey_plan_step_id?: number | null;
  journey_node_id?: number | null;
  /** 同 msgid+跟进人 待确认人数 */
  group_peer_count?: number;
  /** 手册执行说明（旅程触达任务区；可由前端用计划步补全） */
  instruction?: string | null;
};

export type OutboundPeer = {
  id: number;
  external_userid: string;
  contact_id: number | null;
  contact_name: string;
  sender_userid: string;
  sender_name: string;
  status: string;
};

export type WecomOutboundMessagesPayload = {
  outbound_messages: WecomOutboundMessage[];
  pagination: {
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
  };
};

export { ApiError };

export async function listWecomOutboundMessages(
  accountId: string | number,
  opts: { externalUserid?: string; msgid?: string; page?: number; perPage?: number },
): Promise<WecomOutboundMessagesPayload> {
  const params = new URLSearchParams();
  if (opts.externalUserid) params.set("external_userid", opts.externalUserid);
  if (opts.msgid) params.set("msgid", opts.msgid);
  if (opts.page) params.set("page", String(opts.page));
  if (opts.perPage) params.set("per_page", String(opts.perPage));
  return api.fetchForAccount<WecomOutboundMessagesPayload>(
    accountId,
    `/account/wecom_outbound_messages?${params.toString()}`,
  );
}

/** 对该客户发起一条临时外发（企微群发模板；需员工在企微确认） */
export async function createWecomOutboundMessage(
  accountId: string | number,
  body: {
    external_contact_id: number;
    content: string;
    sender_userid?: string;
  },
): Promise<WecomOutboundMessage> {
  const payload = await api.fetchForAccount<{ outbound_message: WecomOutboundMessage }>(
    accountId,
    "/account/wecom_outbound_messages",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  return payload.outbound_message;
}

/** 立即发送旅程计划步（推企微待确认，并 mark PlanStep） */
export async function sendJourneyPlanStepNow(
  accountId: string | number,
  body: {
    journey_plan_step_id: number;
    external_contact_id?: number;
    external_userid?: string;
  },
): Promise<WecomOutboundMessage> {
  const payload = await api.fetchForAccount<{ outbound_message: WecomOutboundMessage }>(
    accountId,
    "/account/journey_plan_step_sends",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  return payload.outbound_message;
}

export type PlanStepScheduleResult = {
  ok: boolean;
  plan_step_id: number;
  planned_at: string;
  planned_on: string;
  reflowed_count: number;
  reflowed: { id: number; planned_at: string; planned_on: string }[];
};

/** 定时改期：只改 planned_at / 连坐后续，不创建出站 */
export async function schedulePlanStep(
  accountId: string | number,
  body: {
    plan_step_id: number;
    planned_at: string;
    external_contact_id?: number;
    external_userid?: string;
  },
): Promise<PlanStepScheduleResult> {
  return api.fetchForAccount<PlanStepScheduleResult>(
    accountId,
    "/account/plan_step_schedules",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
}

/** 异步手动成稿：API 立刻 202；结果经 BackgroundTask / Cable 回写 */
export type ReachOrchestrationAccepted = {
  accepted: boolean;
  status: "queued" | "running" | string;
  job_id: number;
  task_id: number;
  orchestration_key?: string | null;
  task: {
    id: number;
    kind: string;
    status: string;
    title: string;
    detail?: string | null;
    meta?: Record<string, unknown>;
    error_message?: string | null;
  };
};

/** Job 完成后从 task.meta 拼 toast（与旧同步 Result 字段对齐） */
export type ReachOrchestrationResult = {
  ok: boolean;
  orchestration_key: string | null;
  batches: number;
  voided_outbound: number;
  /** 与 voided_outbound 同义，便于前端 */
  voided_count?: number;
  /** batches=0 时说明为何未推企微 */
  skipped_reason?: string | null;
};

/** 从已结束的 reach_draft BackgroundTask 还原 toast 用 Result */
export function reachOrchestrationResultFromTaskMeta(
  meta: Record<string, unknown> | null | undefined,
): ReachOrchestrationResult {
  const m = meta ?? {};
  const batches = Number(m.batches ?? 0);
  const voided = Number(m.voided_outbound ?? m.voided_count ?? 0);
  return {
    ok: true,
    orchestration_key:
      typeof m.orchestration_key === "string" ? m.orchestration_key : null,
    batches: Number.isFinite(batches) ? batches : 0,
    voided_outbound: Number.isFinite(voided) ? voided : 0,
    voided_count: Number.isFinite(voided) ? voided : 0,
    skipped_reason:
      typeof m.skipped_reason === "string" ? m.skipped_reason : null,
  };
}

/** 按 API 字段拼成稿 toast（batches=成稿组数，不再指出站） */
export function reachOrchestrationToastMessage(
  result: ReachOrchestrationResult,
  successToast: string,
): { tone: "success" | "message"; text: string } {
  if (result.batches > 0) {
    return {
      tone: "success",
      text: `${successToast} · 请点「立即发送」推到企微确认`,
    };
  }

  const reason = result.skipped_reason;
  if (reason === "no_candidates") {
    return { tone: "message", text: "无可编排步骤" };
  }
  if (reason === "no_ready_cohorts") {
    return { tone: "message", text: "编排未产出可发送稿（门闸未过或分类跳过）" };
  }
  if (reason === "dispatch_empty") {
    return { tone: "message", text: "编排未产出可用成稿，请稍后重试" };
  }
  return { tone: "message", text: `${successToast} · 未生成新稿` };
}

export async function cancelOutboundMessage(
  accountId: string | number,
  outboundId: number,
  mode: "current" | "all",
): Promise<{ cancelled_ids: number[]; rebuilt_msgid: string | null }> {
  return api.fetchForAccount(
    accountId,
    `/account/wecom_outbound_messages/${outboundId}/cancel`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mode }),
    },
  );
}

export async function listOutboundPeers(
  accountId: string | number,
  outboundId: number,
): Promise<{
  sender_userid: string;
  sender_name: string;
  msgid: string;
  peers: OutboundPeer[];
}> {
  return api.fetchForAccount(
    accountId,
    `/account/wecom_outbound_messages/${outboundId}/peers`,
  );
}

export async function replayPlanStep(
  accountId: string | number,
  body: {
    source_plan_step_id: number;
    reason: "replay" | "reschedule";
    external_userid?: string;
    binding_id?: number;
  },
): Promise<{ plan_step_id: number; needs_ai_draft: boolean }> {
  return api.fetchForAccount(accountId, "/account/plan_step_replays", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function triggerReachOrchestration(
  accountId: string | number,
  body: {
    external_userid?: string;
    external_contact_id?: number;
    binding_id?: number;
    plan_step_id?: number;
    /** true = 重新成稿（可作废旧待确认） */
    supersede?: boolean;
  },
): Promise<ReachOrchestrationAccepted> {
  return api.fetchForAccount<ReachOrchestrationAccepted>(
    accountId,
    "/account/reach_orchestrations",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
}
